import { createContext, useContext } from 'react';
import * as QueryString from 'query-string';
import { TimeRange } from '../types/TimeRange';
import { PERIOD_CUSTOM } from '../component/Constants';
import { Dayjs } from 'dayjs';
import { normalizeTz, resolveUserTimezone, unwrapQueryParam, parseUrlTime, buildRedirectQuery, API_DATE_FORMAT } from '../helpers/timeRangeUtils';

export type SetTimeRangeParamType = Pick<TimeRange, 'timezone' | 'from' | 'to' | 'interval' | 'period'> & { autorefresh?: boolean }

/** If URL has fm/to/tz that differ from user's timezone, return new search string with times converted to user's tz. Otherwise null (no redirect needed). */
export function getConvertedTimeRangeRedirectSearch(search: string): string | null {
    const parsed = QueryString.parse(search);
    if (!parsed.fm || !parsed.to || !parsed.tz) return null;
    const urlTz = unwrapQueryParam(parsed.tz, 'UTC');
    const userTz = resolveUserTimezone();
    if (normalizeTz(userTz) === normalizeTz(urlTz)) return null;
    const fromTime = parseUrlTime(parsed.fm.toString(), urlTz);
    const toTime = parseUrlTime(parsed.to.toString(), urlTz);
    if (!fromTime || !toTime) return null;
    const fromConverted = fromTime.tz(userTz).format(API_DATE_FORMAT);
    const toConverted = toTime.tz(userTz).format(API_DATE_FORMAT);
    const rest = { ...parsed };
    delete rest.fm;
    delete rest.to;
    delete rest.tz;
    const base = `fm=${fromConverted}&to=${toConverted}&tz=${userTz}`;
    const restStr = QueryString.stringify(rest);
    return restStr ? `${base}&${restStr}` : base;
}

/** Merge the current time range into a URL search string, replacing fm/to/tz/interval and keeping all other params. Used to keep the URL in sync when the time range changes. */
export function buildSearchWithTimeRange(search: string, timeRange: { from: Dayjs; to: Dayjs; timezone: string; interval: number }): string {
    const rest = QueryString.parse(search);
    delete rest.fm;
    delete rest.to;
    delete rest.tz;
    delete rest.interval;
    const base = buildRedirectQuery(timeRange);
    const restStr = QueryString.stringify(rest);
    return restStr ? `${base}&${restStr}` : base;
}

/** True when two ranges describe the same URL state: same instants (to the second — the URL format has no milliseconds), timezone and interval. Period is deliberately ignored — the URL doesn't carry it. */
export function timeRangeEqualsUrl(
    a: { from: Dayjs; to: Dayjs; timezone: string; interval: number },
    b: { from: Dayjs; to: Dayjs; timezone: string; interval: number },
): boolean {
    const secs = (d: Dayjs) => Math.floor(d.valueOf() / 1000);
    return secs(a.from) === secs(b.from)
        && secs(a.to) === secs(b.to)
        && normalizeTz(a.timezone) === normalizeTz(b.timezone)
        && a.interval === b.interval;
}

/** Parse fm, to, tz (and optional interval) from URL search string. Used when navigating in-app to sync context from URL. */
export function parseTimeRangeFromSearchParams(search: string): SetTimeRangeParamType | null {
    const parsed = QueryString.parse(search);
    if (!parsed.fm || !parsed.to || !parsed.tz) return null;
    const tz = normalizeTz(unwrapQueryParam(parsed.tz, 'UTC'));
    const intervalParam = unwrapQueryParam(parsed.interval, '0');
    const fromTime = parseUrlTime(parsed.fm.toString(), tz);
    const toTime = parseUrlTime(parsed.to.toString(), tz);
    if (!fromTime || !toTime) return null;
    // Note: autorefresh is intentionally omitted — the URL does not carry it, so the
    // URL-sync should preserve whatever the user currently has set rather than force false.
    return {
        from: fromTime,
        to: toTime,
        timezone: tz,
        interval: parseInt(intervalParam, 10) || 0,
        period: PERIOD_CUSTOM,
    };
}

// Define the context type.
export type TimeRangeContextType = {
    timeRange: TimeRange;
    setTimeRange: (timeRange: SetTimeRangeParamType) => void;
    applyPeriod: (period: number, from: Dayjs, to: Dayjs, timezone?: string, autorefresh?: boolean) => void;
}

// Sentinel default — all methods throw so that "used outside <TimeRangeContext.Provider>" is caught immediately.
const outsideProviderError = () => { throw new Error('TimeRangeContext used outside of its Provider'); };

export const TimeRangeContext = createContext<TimeRangeContextType>({
    timeRange: {} as TimeRange,
    setTimeRange: outsideProviderError,
    applyPeriod: outsideProviderError,
});
TimeRangeContext.displayName = 'TimeRange';

// Export the convenience hook.
export const useTimeRange = () => useContext(TimeRangeContext);
